import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ExternalLink, Loader2, Monitor, Route, Wifi } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/hooks/use-auth'
import { buildAppHref } from '@/lib/auth'
import { entityRefClass, formatBrowserDateTime } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

function formatMac(serial: string | null): string {
  if (!serial) return '—'
  const hex = serial.replace(/[^0-9a-fA-F]/g, '')
  return hex.match(/.{1,2}/g)?.join(':').toUpperCase() ?? serial
}

export function AdminDeviceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const auth = useAuth()

  const { data: device, isLoading, error } = useQuery({
    queryKey: ['admin-device', id],
    queryFn: () => api.adminGetDevice(Number(id)),
    enabled: !!id,
  })

  // Fetch links that reference this device (as from or to)
  const { data: linksFrom } = useQuery({
    queryKey: ['admin-links', 'from-device', id],
    queryFn: () => api.adminListLinks({ page: '1', page_size: '200', from_device_id: id! }),
    enabled: !!id,
  })

  const { data: linksTo } = useQuery({
    queryKey: ['admin-links', 'to-device', id],
    queryFn: () => api.adminListLinks({ page: '1', page_size: '200', to_device_id: id! }),
    enabled: !!id,
  })

  const relatedLinks = [
    ...(linksFrom?.items ?? []),
    ...(linksTo?.items ?? []),
  ].filter((link, i, arr) => arr.findIndex((l) => l.id === link.id) === i)

  if (isLoading) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Loading device...
      </div>
    )
  }

  if (error || !device) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <Button asChild variant="ghost" size="sm">
            <Link to={buildAppHref('/admin/devices', auth)} className="no-underline">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Devices
            </Link>
          </Button>
        </div>
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-destructive">
          {error ? (error as Error).message : 'Device not found'}
        </div>
      </div>
    )
  }

  const fields: [string, React.ReactNode][] = [
    ['ID', device.id],
    ['Serial No', <span key="sn" className="font-mono">{formatMac(device.serial_no)}</span>],
    ['Name', device.name ?? '—'],
    ['Owner', device.owner ?? '—'],
    ['Project', device.project ?? '—'],
    ['Current Location', device.current_location ?? '—'],
    ['Connected', device.connected === true ? 'Yes' : device.connected === false ? 'No' : '—'],
    ['Last Seen', formatBrowserDateTime(device.timeof)],
    ['GPS', device.gps_lat != null && device.gps_long != null ? (
      <a
        key="gps"
        href={`https://www.google.com/maps?q=${device.gps_lat},${device.gps_long}`}
        target="_blank"
        rel="noopener noreferrer"
        className={entityRefClass}
      >
        {device.gps_lat}, {device.gps_long}
      </a>
    ) : '—'],
    ['Host Address', device.host_address ?? '—'],
    ['Device Port', device.device_port ?? '—'],
    ['Modem Port', device.modem_port ?? '—'],
    ['Rego', device.rego ?? '—'],
  ]

  const hostname = device.host_address
    ? new URL(device.host_address.startsWith('http') ? device.host_address : `https://${device.host_address}`).hostname
    : null

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="sm">
          <Link to={buildAppHref('/admin/devices', auth)} className="no-underline">
            <ArrowLeft className="h-4 w-4 mr-1" /> Devices
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">Device #{device.id}</h1>
        {device.name && <span className="text-lg text-muted-foreground">({device.name})</span>}
      </div>

      {/* Host Access buttons */}
      {device.host_address && (
        <Card className="bg-card/95 backdrop-blur-sm">
          <CardContent className="flex items-center gap-3 p-5">
            <span className="text-sm font-medium text-muted-foreground mr-2">Host Access</span>
            {device.device_port != null && (
              <Button asChild variant="outline" size="sm">
                <a
                  href={`${device.host_address.startsWith('http') ? device.host_address : 'https://' + device.host_address}:${device.device_port}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="no-underline"
                >
                  <Monitor className="h-4 w-4" />
                  Device — {hostname}:{device.device_port}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </Button>
            )}
            {device.modem_port != null && (
              <Button asChild variant="outline" size="sm">
                <a
                  href={`${device.host_address.startsWith('http') ? device.host_address : 'https://' + device.host_address}:${device.modem_port}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="no-underline"
                >
                  <Wifi className="h-4 w-4" />
                  Modem — {hostname}:{device.modem_port}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="bg-card/95 backdrop-blur-sm">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <tbody>
              {fields.map(([label, value]) => (
                <tr key={label} className="border-b last:border-b-0">
                  <td className="w-48 px-4 py-3 font-medium text-muted-foreground">{label}</td>
                  <td className="px-4 py-3">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {relatedLinks.length > 0 && (
        <Card className="bg-card/95 backdrop-blur-sm">
          <CardContent className="p-5">
            <h2 className="mb-3 text-lg font-semibold flex items-center gap-2">
              <Route className="h-4 w-4" />
              Related Links ({relatedLinks.length})
            </h2>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>ID</TableHead>
                    <TableHead>Text</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Project</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {relatedLinks.map((link) => (
                    <TableRow key={link.id}>
                      <TableCell>
                        <Link
                          to={buildAppHref(`/admin/links/${link.id}`, auth)}
                          className={entityRefClass}
                        >
                          {link.id}
                        </Link>
                      </TableCell>
                      <TableCell>{link.text ?? '—'}</TableCell>
                      <TableCell>
                        {link.from_device_id != null ? (
                          link.from_device_id === device.id
                            ? <span className="font-medium">#{link.from_device_id} (this)</span>
                            : <Link to={buildAppHref(`/admin/devices/${link.from_device_id}`, auth)} className={entityRefClass}>#{link.from_device_id}</Link>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        {link.to_device_id != null ? (
                          link.to_device_id === device.id
                            ? <span className="font-medium">#{link.to_device_id} (this)</span>
                            : <Link to={buildAppHref(`/admin/devices/${link.to_device_id}`, auth)} className={entityRefClass}>#{link.to_device_id}</Link>
                        ) : '—'}
                      </TableCell>
                      <TableCell>{link.project ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
